import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError, timer, from } from 'rxjs';
import { catchError, map, retry, switchMap } from 'rxjs/operators';
import { DeviceFingerprintService } from './device-fingerprint.service';
import {
  convertDatesFromUtcInPlace,
  convertDatesToUtcInPlace,
} from '../utilities/date-conversion.util';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly maxRetries = 5; // Maximum number of retries
  private readonly backoff = 1000; // Initial delay in milliseconds
  private readonly currentUserKey = 'currentUser';

  constructor(private deviceFingerprintService: DeviceFingerprintService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Get device fingerprint and add it to headers
    return from(this.deviceFingerprintService.generateFingerprint()).pipe(
      switchMap(fingerprint => {
        // Build headers object
        const headers: { [key: string]: string } = {
          'X-Device-Fingerprint': fingerprint,
        };

        // Get subscription key from user (login response) or fallback to environment
        const subscriptionKey = this.getSubscriptionKey();
        if (subscriptionKey) {
          headers['Ocp-Apim-Subscription-Key'] = subscriptionKey;
        }

        // Add Authorization header if user is logged in and request doesn't already have it
        if (!req.headers.has('Authorization')) {
          const token = this.getAuthToken();
          if (token) {
            headers['Authorization'] = token;
          }
        }

        // Normalize outgoing body: every JS Date is rewritten to its UTC ISO
        // string. This enforces the "API always receives UTC" contract
        // regardless of how the calling component prepared the payload.
        // FormData / Blob / primitive bodies are passed through untouched.
        const normalizedBody = convertDatesToUtcInPlace(req.body);

        // Clone the request to add headers and the normalized body.
        const clonedRequest = req.clone({
          setHeaders: headers,
          body: normalizedBody,
        });

        return this.handleRequest(clonedRequest, next);
      })
    );
  }

  /**
   * Get subscription key from user localStorage (from login response)
   * Returns null if user is not logged in (login endpoint uses PublicAuthUrl which doesn't require subscription key)
   */
  private getSubscriptionKey(): string | null {
    try {
      const userJson = localStorage.getItem(this.currentUserKey);
      if (userJson) {
        const user = JSON.parse(userJson);
        if (user && user.subscriptionKey) {
          return user.subscriptionKey;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Get authorization token from localStorage
   * Returns the full 'Bearer xxx' or 'bearer xxx' string
   */
  private getAuthToken(): string | null {
    try {
      const userJson = localStorage.getItem(this.currentUserKey);
      if (!userJson) {
        return null;
      }
      const user = JSON.parse(userJson);
      if (user && user.access_Token && user.token_type) {
        return `${user.token_type} ${user.access_Token}`;
      }
      return null;
    } catch {
      return null;
    }
  }

  private handleRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Handle the HTTP request
    return next.handle(req).pipe(
      // Walk every successful response body and append `Z` to any ISO
      // datetime string that is missing its timezone marker. Strings stay
      // strings — we deliberately do NOT convert them to Date objects,
      // because much of the existing codebase performs string operations
      // (`.split('T')`, `.substring(0, 10)`, etc.) on these fields.
      // After this normalization, any downstream `new Date(stringField)`
      // call parses the value correctly as UTC and renders in the user's
      // local timezone via the `date` pipe / PrimeNG / `toString()`.
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body != null) {
          const converted = convertDatesFromUtcInPlace(event.body);
          // Re-clone only when the converter returned a different reference
          // (top-level string case); otherwise the in-place mutation is
          // already visible on the existing event.body.
          if (converted !== event.body) {
            return event.clone({ body: converted });
          }
        }
        return event;
      }),
      // Retry logic using retry with delay
      retry({
        count: this.maxRetries,
        delay: (error, retryAttempt) => {
          // Only retry if status is 0 (network-related error) or undefined/null
          // Undefined/null status typically indicates network/connection issues
          const errorStatus = error?.status;

          if (
            errorStatus !== 0 &&
            errorStatus !== undefined &&
            errorStatus !== null
          ) {
            console?.error(`Error status ${errorStatus}, not retrying.`);
            throw error; // Propagate error immediately
          }

          const delayDuration = this.backoff * Math.pow(2, retryAttempt - 1); // Exponential backoff
          console.warn(
            `Retry attempt #${retryAttempt} for ${errorStatus === 0 ? 'network error' : 'connection issue'}, delaying for ${delayDuration}ms`
          );
          return timer(delayDuration); // Return an Observable that emits after the delay
        },
      }),
      // Catch errors after all retries are exhausted
      catchError(error => {
        console?.error('HTTP request failed after retries:', error);
        return throwError(() => error);
      })
    );
  }
}
